/**
 * src/hooks/usePWA.js
 * Provides:
 *   - isInstallable  — can show "Add to Home Screen"
 *   - install()      — trigger browser install prompt
 *   - isOnline       — network status
 *   - isStandalone   — running as installed PWA
 *   - swUpdateAvailable — new version of service worker ready
 */
import { useState, useEffect } from 'react';

export function usePWA() {
  const [deferredPrompt,    setDeferredPrompt]    = useState(null);
  const [isInstallable,     setIsInstallable]     = useState(false);
  const [isOnline,          setIsOnline]          = useState(navigator.onLine);
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false);

  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;

  useEffect(() => {
    // Capture the install prompt
    const onBeforeInstall = e => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Detect install completion
    const onInstalled = () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    // Online / offline
    const onOnline  = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);

    // SW update
    const onUpdate = () => setSwUpdateAvailable(true);

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled',        onInstalled);
    window.addEventListener('online',              onOnline);
    window.addEventListener('offline',             onOffline);
    window.addEventListener('sw-update-available', onUpdate);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled',        onInstalled);
      window.removeEventListener('online',              onOnline);
      window.removeEventListener('offline',             onOffline);
      window.removeEventListener('sw-update-available', onUpdate);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setIsInstallable(false);
    return outcome; // 'accepted' | 'dismissed'
  };

  return { isInstallable, install, isOnline, isStandalone, swUpdateAvailable };
}
